import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, CheckCircle2, RefreshCcw, XCircle, Clock, ScaleIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RANGES = [
    { id: '1',    label: 'Today',      days: 1 },
    { id: '7',    label: '7 days',     days: 7 },
    { id: '30',   label: '30 days',    days: 30 },
    { id: 'mtd',  label: 'This month', days: null },
    { id: 'all',  label: '365 days',   days: 365 },
];

const fmtUSD = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Bucket = ({ icon: Icon, label, count, total, color, testid }) => (
    <div
        className="rounded-xl border border-white/10 bg-black/30 p-4 flex flex-col gap-1"
        data-testid={testid}
        style={{ boxShadow: `inset 0 -3px 0 0 ${color}` }}
    >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold" style={{ color }}>
            <Icon className="w-3.5 h-3.5" /> {label}
        </div>
        <div className="font-mono text-2xl font-black text-white">{fmtUSD(total)}</div>
        <div className="text-[11px] text-[#A0AAB2]">{count} request{count === 1 ? '' : 's'}</div>
    </div>
);

export const CashoutAccountingPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState('30');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let url;
            if (range === 'mtd') {
                url = `${API}/admin/cashout-accounting?mtd=1`;
            } else {
                const days = RANGES.find((r) => r.id === range)?.days || 30;
                url = `${API}/admin/cashout-accounting?days=${days}`;
            }
            const { data: resp } = await axios.get(url, { withCredentials: true });
            setData(resp);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load accounting');
        }
        setLoading(false);
    }, [range]);

    useEffect(() => { fetchData(); }, [fetchData]);

    return (
        <div className="card-dark p-6 mb-4" data-testid="cashout-accounting-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                        <ScaleIcon className="w-5 h-5 text-[#FFD700]" />
                        Cashout Accounting · Reconciliation
                    </h3>
                    <p className="text-[11px] text-[#A0AAB2] mt-1">
                        Tracks <span className="text-[#39FF14] font-bold">cash actually paid</span> vs
                        {' '}<span className="text-[#00E0FF] font-bold">played-back credits</span> so the books always match.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex gap-1 bg-black/40 border border-white/10 rounded-lg p-1">
                        {RANGES.map((r) => (
                            <button
                                key={r.id}
                                type="button"
                                onClick={() => setRange(r.id)}
                                data-testid={`cashout-acct-range-${r.id}`}
                                className={`px-3 py-1.5 rounded-md text-[11px] font-bold transition-all ${
                                    range === r.id
                                        ? 'bg-[#FFD700] text-black'
                                        : 'text-[#A0AAB2] hover:text-white'
                                }`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={fetchData}
                        className="p-2 rounded-md border border-white/10 text-[#A0AAB2] hover:text-white hover:bg-white/5"
                        title="Refresh"
                        data-testid="cashout-acct-refresh"
                    >
                        <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {loading && !data ? (
                <div className="flex justify-center py-10"><div className="spinner" /></div>
            ) : data ? (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                        <Bucket
                            icon={DollarSign}
                            label="Requested"
                            count={data.requested.count}
                            total={data.requested.total}
                            color="#FFD700"
                            testid="acct-bucket-requested"
                        />
                        <Bucket
                            icon={CheckCircle2}
                            label="Cash Paid"
                            count={data.cash_paid.count}
                            total={data.cash_paid.total}
                            color="#39FF14"
                            testid="acct-bucket-paid"
                        />
                        <Bucket
                            icon={DollarSign}
                            label="Fees Kept"
                            count={data.fees_kept?.count ?? 0}
                            total={data.fees_kept?.total ?? 0}
                            color="#C084FC"
                            testid="acct-bucket-fees"
                        />
                        <Bucket
                            icon={RefreshCcw}
                            label="Played Back"
                            count={data.played_back.count}
                            total={data.played_back.total}
                            color="#00E0FF"
                            testid="acct-bucket-playedback"
                        />
                        <Bucket
                            icon={XCircle}
                            label="Denied"
                            count={data.denied.count}
                            total={data.denied.total}
                            color="#FF3B30"
                            testid="acct-bucket-denied"
                        />
                        <Bucket
                            icon={Clock}
                            label="Pending"
                            count={data.pending.count}
                            total={data.pending.total}
                            color="#FF8C00"
                            testid="acct-bucket-pending"
                        />
                    </div>

                    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#39FF14]/30 bg-[#39FF14]/5 p-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">Net Cash Out (real money out the door)</div>
                            <div className="font-mono text-3xl font-black text-[#39FF14]" data-testid="acct-net-cash-out">
                                {fmtUSD(data.net_cash_out)}
                            </div>
                            {data.paid_above_net?.total > 0 && (
                                <div className="text-[10px] text-amber-300 font-mono mt-1" data-testid="acct-paid-above-net">
                                    includes {fmtUSD(data.paid_above_net.total)} paid above net on {data.paid_above_net.count} older gross-basis request(s)
                                </div>
                            )}
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold">Reconciliation</div>
                            <div
                                className={`font-bold text-sm inline-flex items-center gap-1.5 ${
                                    data.reconciles ? 'text-[#39FF14]' : 'text-[#FF3B30]'
                                }`}
                                data-testid="acct-reconciles"
                            >
                                {data.reconciles ? (
                                    <>
                                        <CheckCircle2 className="w-4 h-4" /> Books balance · Σ {fmtUSD(data.sum_of_buckets)}
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="w-4 h-4" /> Mismatch — investigate
                                    </>
                                )}
                            </div>
                            <div className="text-[10px] text-[#A0AAB2] mt-0.5">
                                Σ buckets vs Σ requested: {fmtUSD(data.sum_of_buckets)} / {fmtUSD(data.requested.total)}
                                {(data.unexplained || []).length > 0 && ` · ${data.unexplained.length} request(s) don't add up`}
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <p className="text-[#A0AAB2] text-sm">No data.</p>
            )}
        </div>
    );
};

export default CashoutAccountingPanel;
